import React from "react";
import {AppStore} from "./AppStore";
import {DisposeUtils} from "../utils/DisposeUtils";
import {ErrorUtils} from "../utils/ErrorUtils";
import {proto} from "../proto/messages";
import {makeAutoObservable} from "mobx";
import {recording} from "../proto/compiled";
import {MobxUtils} from "../utils/MobxUtils";
import {RecordingsStore} from "./RecordingsStore";

export const RecordingStoreContext = React.createContext<RecordingStore | undefined>(undefined);

export class RecordingStore {
  private disposer = new DisposeUtils();
  private app: AppStore;
  private recordingId: string;
  private loadPending = false;
  loading = false;
  recording?: recording.IGetRecordingResponse;
  annotationItems: recording.IRecordingAnnotation[] = [];
  currentTimeMs = 0;
  videoAspectRatio = 16 / 9; // Default to 16:9, updated when video loads
  selectedAnnotation?: recording.IRecordingAnnotation;
  editingAnnotation?: recording.IRecordingAnnotation;
  savingAnnotation?: recording.IRecordingAnnotation;
  editingRecording = false;
  savingRecording = false;

  constructor(app: AppStore, recordingId: string) {
    this.app = app;
    this.recordingId = recordingId;
    makeAutoObservable(this);
    this.load();
  }

  dispose() {
    this.disposer.dispose();
  }

  async load() {
    if (!this.recordingId) {
      return;
    }
    if (this.loading) {
      this.loadPending = true;
      return;
    }
    this.loading = true;
    await this.app.socketApiConnectedAndAuthenticated;
    const previousSelectedId = this.selectedAnnotation?.id ?? (this.selectedAnnotation as any)?._clientId ?? null;
    const previousEditingId = this.editingAnnotation?.id ?? (this.editingAnnotation as any)?._clientId ?? null;
    const previousSavingId = this.savingAnnotation?.id ?? (this.savingAnnotation as any)?._clientId ?? null;
    try {
      const response = await this.app.socketApi.fetch(
        proto.TxGetRecording.create({id: this.recordingId}),
        new proto.RxGetRecordingResponse(),
      );
      this.recording = response.proto;

      // Make observable
      MobxUtils.makeObservable(this.recording);
      MobxUtils.makeObservable(this.recording?.recording);
      const rawAnnotations = this.recording?.annotations ?? [];
      rawAnnotations.forEach((item) => MobxUtils.makeObservable(item));
      MobxUtils.makeObservable(this.recording.recording?.participants);
      this.recording.recording?.participants?.forEach((participant) => {
        MobxUtils.makeObservable(participant);
      });
      this.annotationItems = rawAnnotations.slice();
      if (this.recording) {
        this.recording.annotations = this.annotationItems;
      }
      this.sortAnnotations();
      this.selectedAnnotation = this.resolveAnnotation(previousSelectedId);
      this.editingAnnotation = this.resolveAnnotation(previousEditingId);
      this.savingAnnotation = this.resolveAnnotation(previousSavingId);
    } catch (e) {
      ErrorUtils.fromFetch(e);
    }
    this.loading = false;
    if (this.loadPending) {
      this.loadPending = false;
      this.load();
    }
  }

  get videoUrl() {
    return this.recording?.videoUrl ?? "";
  }

  get annotations(): recording.IRecordingAnnotation[] {
    return this.annotationItems;
  }

  get hasUnsavedAnnotation() {
    return this.annotationItems.some((annotation) => !annotation.id);
  }

  setCurrentTime(ms: number) {
    this.currentTimeMs = Math.max(0, Math.round(ms));
  }

  setVideoAspectRatio(width: number, height: number) {
    if (width > 0 && height > 0) {
      this.videoAspectRatio = width / height;
    }
  }

  createAnnotation(startMs: number) {
    if (!this.recording) {
      return undefined;
    }
    if (!this.recording.annotations) {
      this.recording.annotations = [];
      MobxUtils.makeObservable(this.recording.annotations);
    }
    const annotation = recording.RecordingAnnotation.create({
      id: "",
      startMs: Math.max(0, Math.round(startMs)),
      endMs: 0,
      label: "",
      description: "",
      published: false,
      annotationType: recording.AnnotationType.AT_NONE,
    });
    (annotation as any)._clientId = `${Date.now()}-${Math.random()}`;
    MobxUtils.makeObservable(annotation);
    this.annotationItems.push(annotation);
    if (this.recording) {
      this.recording.annotations = this.annotationItems;
    }
    this.sortAnnotations();
    this.selectedAnnotation = annotation;
    this.editingAnnotation = annotation;
    return annotation;
  }

  async saveAnnotation(annotation: recording.IRecordingAnnotation) {
    if (!this.recording) {
      return;
    }
    this.savingAnnotation = annotation;
    try {
      const annotationMessage = recording.RecordingAnnotation.create({
        id: annotation.id ?? "",
        startMs: annotation.startMs ?? 0,
        endMs: annotation.endMs ?? 0,
        label: annotation.label ?? "",
        description: annotation.description ?? "",
        published: annotation.published ?? false,
        annotationType: annotation.annotationType ?? recording.AnnotationType.AT_NONE,
      });
      const response = await this.app.socketApi.fetch(
        proto.TxSaveRecordingAnnotation.create({
          recordingId: this.recordingId,
          annotation: annotationMessage,
        }),
        new proto.RxSaveRecordingAnnotationResponse(),
      );
      const saved = response.proto.annotation;
      if (!saved) {
        return;
      }
      MobxUtils.makeObservable(saved);

      Object.assign(annotation, saved);
      this.sortAnnotations();
      this.selectedAnnotation = annotation;
      this.editingAnnotation = undefined;
    } catch (e) {
      ErrorUtils.fromFetch(e);
    } finally {
      this.savingAnnotation = undefined;
    }
  }

  async saveRecording() {
    if (!this.recording?.recording) {
      return;
    }
    this.savingRecording = true;
    try {
      const status = await this.app.socketApi.sendMessage(
        proto.TxUpdateRecording.create({
          recording: this.recording.recording,
        }),
        {ack: true},
      );
      if (!ErrorUtils.fromFetch(status)) {
        return;
      }
      this.editingRecording = false;
      // Emit update to RecordingsStore instances
      RecordingsStore.recordingUpdated.next(this.recording.recording);
    } catch (e) {
      ErrorUtils.fromFetch(e);
    } finally {
      this.savingRecording = false;
    }
  }

  async deleteRecording() {
    if (!this.recordingId) {
      return;
    }
    const status = await this.app.socketApi.sendMessage(
      proto.TxDeleteRecording.create({id: this.recordingId}),
      {ack: true},
    );
    return ErrorUtils.fromFetch(status);
  }

  async deleteAnnotation(annotation: recording.IRecordingAnnotation) {
    if (!annotation.id) {
      const index = this.annotationItems.indexOf(annotation);
      if (index >= 0) {
        this.annotationItems.splice(index, 1);
      }
      if (this.recording) {
        this.recording.annotations = this.annotationItems;
      }
      this.removeSelectionForAnnotation(annotation);
      return;
    }

    try {
      const status = await this.app.socketApi.sendMessage(
        proto.TxDeleteRecordingAnnotation.create({
          recordingId: this.recordingId,
          annotationId: annotation.id,
        }),
        {ack: true},
      );
      if (!ErrorUtils.fromFetch(status)) {
        return;
      }
      const index = this.annotationItems.indexOf(annotation);
      if (index >= 0) {
        this.annotationItems.splice(index, 1);
      } else {
        const matchIndex = this.annotationItems.findIndex((a) => a.id === annotation.id);
        if (matchIndex >= 0) {
          this.annotationItems.splice(matchIndex, 1);
        }
      }
      if (this.recording) {
        this.recording.annotations = this.annotationItems;
      }
      this.removeSelectionForAnnotation(annotation);
    } catch (e) {
      ErrorUtils.fromFetch(e);
    }
  }

  private sortAnnotations() {
    this.annotationItems.sort((a, b) => (a.startMs ?? 0) - (b.startMs ?? 0));
    if (this.recording) {
      this.recording.annotations = this.annotationItems;
    }
  }

  private removeSelectionForAnnotation(annotation: recording.IRecordingAnnotation) {
    if (this.isSameAnnotation(this.selectedAnnotation, annotation)) {
      this.selectedAnnotation = this.annotationItems.length ? this.annotationItems[0] : undefined;
    }
    if (this.isSameAnnotation(this.editingAnnotation, annotation)) {
      this.editingAnnotation = undefined;
    }
    if (this.isSameAnnotation(this.savingAnnotation, annotation)) {
      this.savingAnnotation = undefined;
    }
  }

  private isSameAnnotation(a?: recording.IRecordingAnnotation, b?: recording.IRecordingAnnotation) {
    if (!a || !b) {
      return false;
    }
    if (a === b) {
      return true;
    }
    if (a.id && b.id) {
      return a.id === b.id;
    }
    return (a as any)._clientId && (a as any)._clientId === (b as any)._clientId;
  }

  private resolveAnnotation(id: string | null) {
    if (!id) {
      return undefined;
    }
    return this.annotationItems.find((annotation) => {
      if (annotation.id && annotation.id.length && annotation.id === id) {
        return true;
      }
      return (annotation as any)._clientId === id;
    });
  }
}
